// ═══════════════════════════════════════════════════════════════════════════════
// Email Settings — Platform-wide email configuration
//
// Four tabs:
//   1. Identity       — Default from name, reply-to, footer
//   2. Email Registry — All 14 email types grouped by category with toggles
//   3. Templates      — Edit subject, CTA text, CTA color per email type
//   4. Preview        — Select email type + tenant to preview (placeholder)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, Save, Mail, List, FileEdit, Eye, ChevronDown,
  AlertTriangle, Check, X,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import { usePlatformStore } from '@/lib/store';
import {
  fetchPlatformEmailSettings,
  updatePlatformEmailSettings,
  fetchEmailTemplates,
  updateEmailTemplate,
  toggleEmailTemplate,
} from '@/lib/api';
import type { PlatformEmailSettings, EmailTemplate } from '@/types';

// ── Constants ────────────────────────────────────────────────────────────────

type Tab = 'identity' | 'registry' | 'templates' | 'preview';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'identity', label: 'Identity', icon: <Mail size={14} /> },
  { id: 'registry', label: 'Email Registry', icon: <List size={14} /> },
  { id: 'templates', label: 'Templates', icon: <FileEdit size={14} /> },
  { id: 'preview', label: 'Preview', icon: <Eye size={14} /> },
];

const CATEGORY_ORDER: EmailTemplate['category'][] = ['auth', 'notification', 'system', 'marketing'];

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Authentication',
  notification: 'Notification',
  system: 'System',
  marketing: 'Marketing',
};

const RECIPIENT_BADGE: Record<string, { cls: string; label: string }> = {
  tenant_admin: { cls: 'badge-brand', label: 'Tenant Admin' },
  platform_admin: { cls: 'badge-purple', label: 'Platform Admin' },
  end_user: { cls: 'badge-blue', label: 'End User' },
};

/** Critical email template keys that should warn before disabling. */
const CRITICAL_KEYS = new Set([
  'billing_invoice',
  'billing_payment_failed',
  'auth_magic_link',
  'auth_password_reset',
  'auth_email_verification',
]);

/** Template variables available per category (illustrative). */
const TEMPLATE_VARIABLES: Record<string, string[]> = {
  auth: ['{{tenant_name}}', '{{user_name}}', '{{user_email}}', '{{magic_link}}', '{{reset_link}}'],
  notification: ['{{tenant_name}}', '{{user_name}}', '{{bulletin_title}}', '{{announcement_title}}'],
  system: ['{{tenant_name}}', '{{invoice_number}}', '{{amount}}', '{{plan_name}}'],
  marketing: ['{{tenant_name}}', '{{user_name}}', '{{referral_code}}', '{{referral_link}}'],
};

// ═══════════════════════════════════════════════════════════════════════════════

export function EmailSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('identity');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Email Settings</h1>
        <p className="text-surface-500 mt-1">
          Configure platform-wide email identity, manage templates, and control which emails are sent
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-200 rounded-xl w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-surface-0 text-surface-900 shadow-sm'
                : 'text-surface-500 hover:text-surface-700',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'identity' && <IdentityTab />}
      {activeTab === 'registry' && <RegistryTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'preview' && <PreviewTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Identity
// ═══════════════════════════════════════════════════════════════════════════════

function IdentityTab() {
  const [settings, setSettings] = useState<PlatformEmailSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [footerText, setFooterText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlatformEmailSettings();
      setSettings(data);
      if (data) {
        setFromName(data.default_from_name || '');
        setReplyTo(data.default_reply_to || '');
        setFooterText(data.email_footer_text || '');
      }
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await updatePlatformEmailSettings({
        default_from_name: fromName,
        default_reply_to: replyTo || null,
        email_footer_text: footerText || null,
      });
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-16">
        <Mail size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No email settings found.</p>
        <p className="text-xs text-surface-400 mt-1">
          Run the email settings migration to initialise defaults.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel p-6 max-w-2xl space-y-5">
      <h2 className="text-lg font-semibold text-surface-900">Email Identity</h2>
      <p className="text-xs text-surface-500">
        These defaults apply to all platform-sent emails unless overridden at the template level.
      </p>

      {/* Default From Name */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">Default From Name</label>
        <input
          type="text"
          value={fromName}
          onChange={e => setFromName(e.target.value)}
          placeholder="e.g. UnRavel Platform"
          className="input-field w-full"
        />
      </div>

      {/* From Email (read-only) */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">From Email</label>
        <input
          type="email"
          value={settings.default_from_email}
          readOnly
          className="input-field w-full bg-surface-50 text-surface-500 cursor-not-allowed"
        />
        <p className="text-[10px] text-surface-400">
          This is the Resend-verified sender address and cannot be changed here.
        </p>
      </div>

      {/* Reply-To */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">Reply-To</label>
        <input
          type="email"
          value={replyTo}
          onChange={e => setReplyTo(e.target.value)}
          placeholder="support@yourdomain.com"
          className="input-field w-full"
        />
      </div>

      {/* Footer Text */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">Footer Text</label>
        <textarea
          value={footerText}
          onChange={e => setFooterText(e.target.value)}
          rows={3}
          placeholder="Displayed at the bottom of every platform email..."
          className="input-field w-full resize-none"
        />
      </div>

      {/* Error / success */}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <X size={12} /> {error}
        </p>
      )}

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className={cn(
          'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all',
          saved
            ? 'bg-emerald-600 text-white'
            : 'bg-brand-500 hover:bg-brand-600 text-white',
          saving && 'opacity-60 cursor-not-allowed',
        )}
      >
        {saving ? (
          <Loader2 size={14} className="animate-spin" />
        ) : saved ? (
          <Check size={14} />
        ) : (
          <Save size={14} />
        )}
        {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Email Registry
// ═══════════════════════════════════════════════════════════════════════════════

function RegistryTab() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await fetchEmailTemplates());
    } catch (err) {
      console.error('Failed to load email templates:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, EmailTemplate[]>();
    for (const cat of CATEGORY_ORDER) {
      map.set(cat, templates.filter(t => t.category === cat));
    }
    return map;
  }, [templates]);

  const handleToggle = async (template: EmailTemplate) => {
    const newVal = !template.is_platform_enabled;

    // If turning OFF a critical email, require confirmation
    if (!newVal && CRITICAL_KEYS.has(template.template_key)) {
      setConfirmDisable(template.id);
      return;
    }

    await doToggle(template.id, newVal);
  };

  const doToggle = async (id: string, enabled: boolean) => {
    setTogglingId(id);
    setConfirmDisable(null);
    try {
      await toggleEmailTemplate(id, enabled);
      setTemplates(prev =>
        prev.map(t => (t.id === id ? { ...t, is_platform_enabled: enabled } : t)),
      );
    } catch (err) {
      console.error('Failed to toggle template:', err);
    }
    setTogglingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-16">
        <Mail size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No email templates found.</p>
        <p className="text-xs text-surface-400 mt-1">
          Seed the email_templates table to populate this registry.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Confirmation dialog overlay */}
      {confirmDisable && (
        <ConfirmDisableDialog
          template={templates.find(t => t.id === confirmDisable)!}
          onConfirm={() => doToggle(confirmDisable, false)}
          onCancel={() => setConfirmDisable(null)}
        />
      )}

      <div className="glass-panel overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-surface-200">
              <th className="table-header-cell w-48">Email Type</th>
              <th className="table-header-cell">Description</th>
              <th className="table-header-cell w-32">Recipient</th>
              <th className="table-header-cell w-20 text-center">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORY_ORDER.map(cat => {
              const items = grouped.get(cat) || [];
              if (items.length === 0) return null;
              return (
                <CategoryGroup
                  key={cat}
                  category={cat}
                  items={items}
                  togglingId={togglingId}
                  onToggle={handleToggle}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoryGroup({ category, items, togglingId, onToggle }: {
  category: string;
  items: EmailTemplate[];
  togglingId: string | null;
  onToggle: (t: EmailTemplate) => void;
}) {
  return (
    <>
      {/* Category header row */}
      <tr className="bg-surface-50 border-b border-surface-200">
        <td colSpan={4} className="px-4 py-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-surface-500">
            {CATEGORY_LABELS[category] || category}
          </span>
        </td>
      </tr>
      {items.map(template => {
        const badge = RECIPIENT_BADGE[template.recipient_type] || RECIPIENT_BADGE.end_user;
        const toggling = togglingId === template.id;
        return (
          <tr key={template.id} className="table-row">
            <td className="table-cell">
              <span className="text-sm font-medium text-surface-900">{template.display_name}</span>
            </td>
            <td className="table-cell">
              <span className="text-xs text-surface-500" title={template.description}>
                {template.description}
              </span>
            </td>
            <td className="table-cell">
              <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', badge.cls)}>
                {badge.label}
              </span>
            </td>
            <td className="table-cell text-center">
              <button
                onClick={() => onToggle(template)}
                disabled={toggling}
                className="relative inline-flex items-center"
                aria-label={template.is_platform_enabled ? 'Disable' : 'Enable'}
              >
                <div
                  className={cn(
                    'w-9 h-5 rounded-full transition-colors',
                    template.is_platform_enabled ? 'bg-brand-500' : 'bg-surface-300',
                    toggling && 'opacity-50',
                  )}
                >
                  <div
                    className={cn(
                      'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                      template.is_platform_enabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </div>
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

function ConfirmDisableDialog({ template, onConfirm, onCancel }: {
  template: EmailTemplate;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="glass-panel p-5 border-amber-500/40 space-y-3">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-500/10">
          <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-surface-900">
            Disable critical email?
          </h3>
          <p className="text-xs text-surface-500 mt-1">
            You are about to disable <strong className="text-surface-700">{template.display_name}</strong>.
            This will prevent <strong className="text-surface-700">ALL tenants</strong> from receiving this email.
          </p>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-1.5 text-xs font-medium text-surface-500 hover:text-surface-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
        >
          Yes, Disable
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — Templates
// ═══════════════════════════════════════════════════════════════════════════════

function TemplatesTab() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await fetchEmailTemplates());
    } catch (err) {
      console.error('Failed to load email templates:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (updated: EmailTemplate) => {
    setTemplates(prev => prev.map(t => (t.id === updated.id ? updated : t)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-16">
        <FileEdit size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No email templates found.</p>
      </div>
    );
  }

  return (
    <div className="glass-panel overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-surface-200">
            <th className="table-header-cell w-48">Email Type</th>
            <th className="table-header-cell">Category</th>
            <th className="table-header-cell w-32">Recipient</th>
            <th className="table-header-cell w-10"></th>
          </tr>
        </thead>
        <tbody>
          {templates.map(template => {
            const expanded = expandedId === template.id;
            const badge = RECIPIENT_BADGE[template.recipient_type] || RECIPIENT_BADGE.end_user;
            return (
              <TemplateRow
                key={template.id}
                template={template}
                expanded={expanded}
                badge={badge}
                onToggle={() => setExpandedId(expanded ? null : template.id)}
                onSaved={handleSaved}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TemplateRow({ template, expanded, badge, onToggle, onSaved }: {
  template: EmailTemplate;
  expanded: boolean;
  badge: { cls: string; label: string };
  onToggle: () => void;
  onSaved: (t: EmailTemplate) => void;
}) {
  const [subject, setSubject] = useState(template.subject_template || '');
  const [heading, setHeading] = useState(template.default_heading || '');
  const [subtitle, setSubtitle] = useState(template.default_subtitle || '');
  const [body, setBody] = useState(template.default_body || '');
  const [ctaText, setCtaText] = useState(template.cta_text || '');
  const [ctaColor, setCtaColor] = useState(template.cta_color || '#2AB3B1');
  const [saving, setSaving] = useState(false);
  const [saved, setSavedState] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state when template changes
  useEffect(() => {
    setSubject(template.subject_template || '');
    setHeading(template.default_heading || '');
    setSubtitle(template.default_subtitle || '');
    setBody(template.default_body || '');
    setCtaText(template.cta_text || '');
    setCtaColor(template.cta_color || '#2AB3B1');
  }, [template]);

  const handleSave = async () => {
    setSaving(true);
    setSavedState(false);
    setError(null);
    try {
      const updated = await updateEmailTemplate(template.id, {
        subject_template: subject || null,
        default_heading: heading || null,
        default_subtitle: subtitle || null,
        default_body: body || null,
        cta_text: ctaText || null,
        cta_color: ctaColor || null,
      });
      onSaved(updated);
      setSavedState(true);
      setTimeout(() => setSavedState(false), 2500);
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  // Use available_variables from the template itself (populated by migration 029)
  const variables: string[] = template.available_variables || [];

  return (
    <>
      <tr className="table-row cursor-pointer" onClick={onToggle}>
        <td className="table-cell">
          <span className="text-sm font-medium text-surface-900">{template.display_name}</span>
        </td>
        <td className="table-cell">
          <span className="text-xs text-surface-500 capitalize">{template.category}</span>
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', badge.cls)}>
            {badge.label}
          </span>
        </td>
        <td className="table-cell">
          <ChevronDown className={cn('w-3 h-3 text-surface-400 transition-transform', expanded && 'rotate-180')} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="px-5 py-4 bg-surface-50 border-b border-surface-200">
            <div className="space-y-4 max-w-xl">
              {/* Subject template */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-surface-700">Subject Template</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. Welcome to {{tenant_name}}"
                  className="input-field w-full"
                />
              </div>

              {/* Default Heading */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-surface-700">Default Heading</label>
                <input
                  type="text"
                  value={heading}
                  onChange={e => setHeading(e.target.value)}
                  placeholder="e.g. New Booking Inquiry!"
                  className="input-field w-full"
                />
                <p className="text-[10px] text-surface-400">
                  The main heading shown in the email card. Tenants can override this.
                </p>
              </div>

              {/* Default Subtitle */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-surface-700">Default Subtitle</label>
                <input
                  type="text"
                  value={subtitle}
                  onChange={e => setSubtitle(e.target.value)}
                  placeholder="e.g. A traveler has submitted an inquiry..."
                  className="input-field w-full"
                />
              </div>

              {/* Default Body */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-surface-700">Default Body</label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={3}
                  placeholder="Optional body text before the dynamic content..."
                  className="input-field w-full resize-none"
                />
              </div>

              {/* CTA Text */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-surface-700">CTA Button Text</label>
                <input
                  type="text"
                  value={ctaText}
                  onChange={e => setCtaText(e.target.value)}
                  placeholder="e.g. Get Started"
                  className="input-field w-full"
                />
              </div>

              {/* CTA Color */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-surface-700">CTA Button Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={ctaColor}
                    onChange={e => setCtaColor(e.target.value)}
                    className="w-8 h-8 rounded border border-surface-300 cursor-pointer bg-transparent"
                  />
                  <input
                    type="text"
                    value={ctaColor}
                    onChange={e => setCtaColor(e.target.value)}
                    placeholder="#2AB3B1"
                    className="input-field w-32 font-mono text-xs"
                  />
                  <div
                    className="px-3 py-1 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: ctaColor }}
                  >
                    {ctaText || 'Preview'}
                  </div>
                </div>
              </div>

              {/* Template variables (from available_variables column) */}
              {variables.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-surface-500">Available Variables</label>
                  <div className="flex flex-wrap gap-1.5">
                    {variables.map((v: string) => (
                      <span
                        key={v}
                        className="text-[10px] px-2 py-0.5 rounded bg-surface-100 text-surface-600 font-mono"
                      >
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tenant visibility badge */}
              <div className="flex items-center gap-2 pt-1">
                <span className={cn(
                  'text-[10px] px-2 py-0.5 rounded-full font-medium',
                  template.tenant_visible
                    ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-500'
                    : 'bg-surface-200 text-surface-500',
                )}>
                  {template.tenant_visible ? 'Visible to tenants' : 'Super admin only'}
                </span>
              </div>

              {/* Error */}
              {error && (
                <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <X size={12} /> {error}
                </p>
              )}

              {/* Save */}
              <button
                onClick={handleSave}
                disabled={saving}
                className={cn(
                  'flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all',
                  saved
                    ? 'bg-emerald-600 text-white'
                    : 'bg-brand-500 hover:bg-brand-600 text-white',
                  saving && 'opacity-60 cursor-not-allowed',
                )}
              >
                {saving ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : saved ? (
                  <Check size={12} />
                ) : (
                  <Save size={12} />
                )}
                {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4 — Preview
// ═══════════════════════════════════════════════════════════════════════════════

function PreviewTab() {
  const { tenants, loadTenants } = usePlatformStore();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');

  useEffect(() => { loadTenants(); }, [loadTenants]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setTemplates(await fetchEmailTemplates());
      } catch (err) {
        console.error('Failed to load email templates:', err);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  const chosen = templates.find(t => t.id === selectedTemplate);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Selectors */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 space-y-1.5">
          <label className="text-xs font-medium text-surface-700">Email Type</label>
          <select
            value={selectedTemplate}
            onChange={e => setSelectedTemplate(e.target.value)}
            className="input-field w-full py-1.5 text-sm"
          >
            <option value="">Select an email type...</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.display_name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 space-y-1.5">
          <label className="text-xs font-medium text-surface-700">Tenant</label>
          <select
            value={selectedTenant}
            onChange={e => setSelectedTenant(e.target.value)}
            className="input-field w-full py-1.5 text-sm"
          >
            <option value="">Select a tenant...</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Preview area */}
      <div className="glass-panel p-8 min-h-[280px] flex flex-col items-center justify-center text-center">
        {selectedTemplate && selectedTenant ? (
          <div className="space-y-3">
            <Mail size={36} className="mx-auto text-surface-400" />
            <p className="text-sm font-medium text-surface-700">
              {chosen?.display_name || 'Selected email'}
            </p>
            <p className="text-xs text-surface-500 max-w-md">
              Email preview will render here when email infrastructure is deployed.
              The selected template and tenant branding will be merged to show a
              realistic preview of the outbound email.
            </p>
            <div className="pt-2">
              <span className="text-[10px] px-3 py-1 rounded-full bg-surface-100 text-surface-400">
                Coming soon
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Eye size={36} className="mx-auto text-surface-300" />
            <p className="text-sm text-surface-500">
              Select an email type and tenant above to preview
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
